'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import { Checkbox, FlexContainer } from '../../components';
import { Label } from '../../typography';
import { useTableFilters } from '../useTableFilters';
import type { ComponentProps } from 'react';
import type { BaseTableFilterProps, FilterOption } from './types';
export interface CheckboxTableFilterProps extends BaseTableFilterProps {
  filterOptions: readonly FilterOption[];
  searchParam: string;
  variant: 'checkbox';
  componentProps?: Partial<ComponentProps<typeof Checkbox>>;
  /**
   * `['true']` renders the box checked while its param is absent, matching the array
   * convention `sanitizeSearchParams` reads. Unchecking then writes an explicit `false`
   * (which `sanitizeSearchParams` accepts for a default-checked box, declared as an
   * option or not), and re-checking removes the param so the URL stays clean in the
   * default state. Typed as the one meaningful value: an unchecked default is simply
   * the absence of this prop.
   */
  defaultValue?: readonly ['true'];
  groups?: never;
  placeholder?: never;
  value?: never;
  fromDateParam?: never;
  toDateParam?: never;
}
export function CheckboxTableFilter(t0) {
  const $ = _c(24);
  const {
    className,
    searchParam,
    searchParamsToClearOnChange,
    componentProps,
    defaultValue,
    filterOptions,
    filterType: t1
  } = t0;
  const filterType = t1 === undefined ? "rsc" : t1;
  const searchParams = useSearchParams();
  const {
    updateFilterValue
  } = useTableFilters(filterType);
  const isCheckedByDefault = defaultValue?.[0] === "true";
  const [isChecked, setIsChecked] = useState(false);
  let t2;
  let t3;
  if ($[0] !== isCheckedByDefault || $[1] !== searchParam || $[2] !== searchParams) {
    t2 = () => {
      const urlValue = searchParams?.get(searchParam);
      setIsChecked(urlValue === null || urlValue === undefined ? isCheckedByDefault : urlValue === "true");
    };
    t3 = [searchParams, searchParam, isCheckedByDefault];
    $[0] = isCheckedByDefault;
    $[1] = searchParam;
    $[2] = searchParams;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== isCheckedByDefault || $[6] !== searchParam || $[7] !== searchParamsToClearOnChange || $[8] !== updateFilterValue) {
    t4 = function handleChange(checked) {
      setIsChecked(checked);
      const value = checked === isCheckedByDefault ? null : checked ? "true" : "false";
      updateFilterValue(searchParam, value, searchParamsToClearOnChange);
    };
    $[5] = isCheckedByDefault;
    $[6] = searchParam;
    $[7] = searchParamsToClearOnChange;
    $[8] = updateFilterValue;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleChange = t4;
  const checkboxFilterOption = filterOptions?.[0];
  if (!checkboxFilterOption) {
    return null;
  }
  let t5;
  if ($[10] !== className) {
    t5 = twMerge("gap-2", className);
    $[10] = className;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== componentProps || $[13] !== handleChange || $[14] !== isChecked || $[15] !== searchParam) {
    t6 = <Checkbox checked={isChecked} id={searchParam} onCheckedChange={handleChange} {...componentProps} />;
    $[12] = componentProps;
    $[13] = handleChange;
    $[14] = isChecked;
    $[15] = searchParam;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== checkboxFilterOption.label || $[18] !== searchParam) {
    t7 = <Label className="cursor-pointer" htmlFor={searchParam}>{checkboxFilterOption.label}</Label>;
    $[17] = checkboxFilterOption.label;
    $[18] = searchParam;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = <FlexContainer className={t5} items="center">{t6}{t7}</FlexContainer>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}